import { useCallback, useRef, useState, useEffect } from 'react'
import {
  Property,
  PropertyValue,
  AssignedPropertyValue,
  PropertyClient,
  AssigneeType,
  VirtualAssignedProperty,
  AssignedValueType,
} from 'api/clients/PropertyClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { useDispatch } from 'react-redux'
import { upsertFilesProperties, deleteFilesProperties } from 'redux/actions/filesProperties'

interface Props {
  assignees: {
    id: string
    type: AssigneeType
  }[]
}

type RemovePropertyOptions = {
  only_empty?: boolean
}

export const useMultipleAssigneesProperties = ({ assignees }: Props) => {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [assignedProperties, setAssignedProperties] = useState<VirtualAssignedProperty[]>([])
  const client = PropertyClient.getBatchAssignedPropertyValues(assignees)
  const assignedPropertiesClient = useRef(client)

  const assigneesIds = assignees.map(({ id }) => id)

  const refreshList = () => {
    resetClient()
    loadMore(true)
  }

  useEffect(() => {
    refreshList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(assigneesIds)])

  const resetClient = useCallback(() => {
    assignedPropertiesClient.current = PropertyClient.getBatchAssignedPropertyValues(assignees)
  }, [assignees])

  const removePropertyFromAssignees = async (propertyId?: number, options: RemovePropertyOptions = {}) => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const response = await PropertyClient.bulkDeleteAssignedProperties(assignees, propertyId!, options)
    if (response && response.type === SUCCESS_RESPONSE_TYPE) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dispatch(deleteFilesProperties(assignees, propertyId!))
      refreshList()
    } else {
      showErrorMessageToast('An error occurred! Our team has been notified and we are looking into it.')
    }
  }

  const addPropertyValueToAssignedProperties = async (_: number | undefined, propertyValue: PropertyValue) => {
    return updateAssignedProperties(undefined, { id: propertyValue.property_id } as Property, [
      { assigned_value_type: AssignedValueType.PropertyValue, assigned_value_id: propertyValue.id as number },
    ])
  }

  const updateAssignedProperties = async (
    _: number | undefined,
    property: Property,
    assignedPropertyValues: AssignedPropertyValue[],
  ) => {
    const response = await PropertyClient.bulkUpdateAssignedProperties(assignees, assignedPropertyValues, property.id)
    if (response && response.type === SUCCESS_RESPONSE_TYPE) {
      dispatch(upsertFilesProperties(response.data.assigned_properties))
      refreshList()
    } else {
      showErrorMessageToast('An error occurred! Our team has been notified and we are looking into it.')
    }
  }

  const updateProperty = () => {
    refreshList()
  }

  const addPropertyToAssignees = async (property: Property) => {
    setAssignedProperties([...assignedProperties, { property, property_values: [] }])
  }

  const loadMore = useCallback(
    async (reset: boolean = false) => {
      setLoading(true)
      const response = await assignedPropertiesClient.current.loadMore()
      if (response && response.type === SUCCESS_RESPONSE_TYPE) {
        const {
          data: { assigned_properties },
        } = response
        setAssignedProperties(
          reset ? assigned_properties : (oldProperties) => oldProperties.concat(assigned_properties),
        )
      } else {
        showErrorMessageToast('An error occurred! Our team has been notified and we are looking into it.')
      }
      setLoading(false)
    },
    [assignedPropertiesClient],
  )

  return {
    assignedProperties,
    hasMore: assignedPropertiesClient.current.hasMore,
    loading,
    loadMore,
    addPropertyToAssignees,
    updateAssignedProperties,
    updateProperty,
    removePropertyFromAssignees,
    addPropertyValueToAssignedProperties,
  }
}
